import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSessionUser } from '../../services/userService';
import '../../cms.css';
import { 
    ArrowLeft, Save, Loader2, Check, AlertTriangle, CheckCircle, Link, ImageIcon, X,
    Settings, Sliders, Copy, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';
import { UserProfile } from '../../types';
import { useSeo } from '../../hooks/useSeo';
import MediaLibraryModal from './MediaLibraryModal';
import { TfidfProcessor } from '../../utils/tfidf';

interface MediaItem {
    id: number;
    url: string;
    url_webp?: string;
    type: number;
    size?: number;
}

interface BlogCategory {
    id: number;
    name: string;
    parent_id: number | null;
    description?: string;
    short_description?: string;
}

// A simple debounce hook
const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

// Slugify function to create clean URLs
const slugify = (str: string) => {
    str = str.replace(/^\s+|\s+$/g, ''); // trim
    str = str.toLowerCase();
    const charMap: { [key: string]: string } = { 'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ż': 'z', 'ź': 'z', 'à': 'a', 'á': 'a', 'ä': 'a', 'â': 'a', 'ã': 'a', 'å': 'a', 'æ': 'a', 'è': 'e', 'é': 'e', 'ë': 'e', 'ê': 'e', 'ì': 'i', 'í': 'i', 'ï': 'i', 'î': 'i', 'ñ': 'n', 'ò': 'o', 'ö': 'o', 'ô': 'o', 'õ': 'o', 'ø': 'o', 'š': 's', 'ù': 'u', 'ú': 'u', 'ü': 'u', 'û': 'u', 'ç': 'c', '·': '-', '/': '-', '_': '-', ',': '-', ':': '-', ';': '-' };
    const regex = new RegExp(Object.keys(charMap).map(c => c.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|'), 'g');
    str = str.replace(regex, (matched) => charMap[matched as keyof typeof charMap]);
    str = str.replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
    return str;
};

const formatBytes = (bytes?: number) => {
    if (bytes === undefined) return 'N/A';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const toDateTimeLocal = (ts: number | null) => {
    if (!ts) return '';
    const date = new Date(ts);
    const tzoffset = (new Date()).getTimezoneOffset() * 60000;
    const localISOTime = (new Date(date.getTime() - tzoffset)).toISOString().slice(0, 16);
    return localISOTime;
};

// Advanced Code Editor with syntax highlighting
const CodeEditor: React.FC<{ value: string; onChange: (val: string) => void }> = ({ value, onChange }) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const preRef = useRef<HTMLPreElement>(null);
    const linesRef = useRef<HTMLDivElement>(null);

    const handleScroll = () => {
        if (textareaRef.current && preRef.current && linesRef.current) {
            preRef.current.scrollTop = textareaRef.current.scrollTop;
            preRef.current.scrollLeft = textareaRef.current.scrollLeft;
            linesRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            const start = e.currentTarget.selectionStart;
            const end = e.currentTarget.selectionEnd;
            const spaces = '  ';
            const newValue = value.substring(0, start) + spaces + value.substring(end);
            onChange(newValue);
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + spaces.length;
                }
            }, 0);
        }
    };

    const lineCount = useMemo(() => value.split('\n').length, [value]);

    const highlightedCode = useMemo(() => {
        if (!value) return '';
        let html = value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        html = html.replace(/(&lt;\/?[a-z0-9]+)(?![^<]*&gt;)/gi, '<span class="token-tag">$1</span>');
        html = html.replace(/([a-z-]+)=/gi, '<span class="token-attr">$1</span>=');
        html = html.replace(/(".*?")/g, '<span class="token-string">$1</span>');
        html = html.replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="token-comment">$1</span>');
        html = html.replace(/(\/?&gt;)/g, '<span class="token-tag">$1</span>');
        return html;
    }, [value]);

    return (
        <div className="code-editor-container border border-gray-700 h-full min-h-[500px]">
            <div className="code-editor-lines" ref={linesRef}>
                {Array.from({ length: lineCount }).map((_, i) => (
                    <div key={i}>{i + 1}</div>
                ))}
            </div>
            <pre className="code-editor-pre no-scrollbar" ref={preRef} aria-hidden="true">
                <code dangerouslySetInnerHTML={{ __html: highlightedCode + '<br />' }} />
            </pre>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onScroll={handleScroll}
                spellCheck="false"
                className="code-editor-textarea no-scrollbar"
                placeholder="<p>Enter HTML content here...</p>"
            />
        </div>
    );
};


const ArticleEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useMemo(() => getSessionUser(), []);

    // Form State
    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [summary, setSummary] = useState('');
    const [content, setContent] = useState('');
    const [status, setStatus] = useState('published');
    const [thumbnailId, setThumbnailId] = useState<number | null>(null);
    const [selectedAuthorIds, setSelectedAuthorIds] = useState<number[]>(id ? [] : (user?.id ? [user.id] : []));
    const [publishedAt, setPublishedAt] = useState<string>('');
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [allCategories, setAllCategories] = useState<BlogCategory[]>([]);
    const [prevArticleId, setPrevArticleId] = useState<number | null>(null);
    const [nextArticleId, setNextArticleId] = useState<number | null>(null);
    const [authorSearch, setAuthorSearch] = useState('');

    // Media State
    const [mediaLibrary, setMediaLibrary] = useState<MediaItem[]>([]);
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);

    // Thumbnail Editing State
    const [isEditingThumbnail, setIsEditingThumbnail] = useState(false);
    const [optimizeQuality, setOptimizeQuality] = useState(80);
    const [isOptimizing, setIsOptimizing] = useState(false);
    const [isGeneratingWebp, setIsGeneratingWebp] = useState(false);
    const [thumbnailCopied, setThumbnailCopied] = useState(false);

    // Slug Validation State
    const debouncedSlug = useDebounce(slug, 500);
    const [slugStatus, setSlugStatus] = useState<'checking' | 'available' | 'taken' | 'idle'>('idle');
    const [slugError, setSlugError] = useState('');

    // Category Suggestions State
    const [suggestedCategories, setSuggestedCategories] = useState<{ id: number; name: string; score: number; confidence: number }[]>([]);
    const [isSuggesting, setIsSuggesting] = useState(false);
    
    // TF-IDF Processor Memoization
    const tfidfProcessor = useMemo(() => {
        if (allCategories.length > 0) {
            const categoryDocs = allCategories.map(cat => ({ id: cat.id, name: cat.name, description: cat.description || '', short_description: cat.short_description || '' }));
            return new TfidfProcessor(categoryDocs);
        }
        return null;
    }, [allCategories]);

    // General State
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const adminPath = location.pathname.split('/')[1];
    
    // Set Title
    useSeo({ title: `CMS - ${id ? `Edit Article #${id}` : 'New Article'} - esoteric.pl` });

    const getAncestors = (childId: number, categories: BlogCategory[]): number[] => {
        const ancestors: number[] = [];
        let currentCat = categories.find(c => c.id === childId);
        while (currentCat && currentCat.parent_id) {
            const parentId = currentCat.parent_id;
            ancestors.push(parentId);
            currentCat = categories.find(c => c.id === parentId);
        }
        return ancestors;
    };

    const getDescendants = (parentId: number, categories: BlogCategory[]): number[] => {
        const descendants: number[] = [];
        const children = categories.filter(c => c.parent_id === parentId);
        for (const child of children) {
            descendants.push(child.id);
            descendants.push(...getDescendants(child.id, categories));
        }
        return descendants;
    };

    const loadArticle = async (articleId: string) => {
        try {
            const res = await fetch(`/api/cms/blog?id=${articleId}`);
            if (res.ok) {
                const data = await res.json();
                setTitle(data.title);
                setSlug(data.slug || slugify(data.title));
                setSummary(data.summary || '');
                setContent(data.content_html || '');
                setStatus(data.status || 'draft');
                setThumbnailId(data.media);
                try {
                    const ids = JSON.parse(data.author_id || '[]');
                    if (Array.isArray(ids)) {
                        setSelectedAuthorIds(ids.map(String).map(Number));
                    }
                } catch {
                    setSelectedAuthorIds([]);
                }
                setPublishedAt(toDateTimeLocal(data.published_at));
                setSelectedCategories(data.categories || []);
                setPrevArticleId(data.prev_id);
                setNextArticleId(data.next_id);
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        if (!user || user.rank !== 0) {
            navigate('/404');
            return;
        }
        
        const fetchUsers = async () => {
            const res = await fetch('/api/users', { headers: { 'x-admin-phone': user.phone }});
            if (res.ok) setUsers(await res.json());
        };

        loadResources();
        fetchUsers();

        if (id) {
            loadArticle(id);
        } else {
            setPublishedAt(toDateTimeLocal(Date.now()));
        }
    }, [id, user, navigate]);
    
    useEffect(() => {
        if (!id) {
            setSlug(slugify(title));
        }
    }, [title, id]);

    useEffect(() => {
        if (debouncedSlug) {
            validateSlug(debouncedSlug);
        } else {
            setSlugStatus('idle');
        }
    }, [debouncedSlug, id]);
    
    useEffect(() => {
        if (tfidfProcessor && (title || content)) {
            runSuggestions();
        }
    }, [tfidfProcessor, id]);

    const loadResources = async () => {
        try {
            const [medRes, catRes] = await Promise.all([
                fetch('/api/cms/media', { headers: { 'x-admin-phone': user?.phone || '' }}),
                fetch('/api/cms/blog-categories')
            ]);
            if (medRes.ok) setMediaLibrary(await medRes.json());
            if (catRes.ok) setAllCategories(await catRes.json());
        } catch (e) { console.error(e); }
    };
    
    const validateSlug = async (slugToCheck: string) => {
        setSlugStatus('checking');
        setSlugError('');
        try {
            const res = await fetch(`/api/slugs?check=${slugToCheck}&currentId=${id}&type=article`);
            const data = await res.json();
            if (data.available) {
                setSlugStatus('available');
            } else {
                setSlugStatus('taken');
                setSlugError(`Slug taken by a ${data.type}`);
            }
        } catch (_e) {
            setSlugStatus('idle');
            setSlugError('Validation error');
        }
    };

    const handleSave = async () => {
        if (slugStatus === 'taken') {
            alert("Slug is already taken. Please choose another one.");
            return;
        }
        setIsLoading(true);
        setIsSuccess(false);

        const payload = {
            id: id ? parseInt(id) : undefined,
            title, slug, summary, content_html: content, status, media: thumbnailId,
            author_id: JSON.stringify(selectedAuthorIds.map(String)), 
            published_at: publishedAt ? new Date(publishedAt).getTime() : null,
            categories: selectedCategories
        };

        const method = id ? 'PUT' : 'POST';
        try {
            const res = await fetch('/api/cms/blog', {
                method,
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': user?.phone || '' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const responseData = await res.json();
                setIsSuccess(true);
                if (!id && responseData.id) {
                    navigate(`/${adminPath}/blog/edit/${responseData.id}`, { replace: true });
                }
                setTimeout(() => setIsSuccess(false), 2000);
            } else {
                alert('Save failed');
            }
        } catch (e) { console.error(e); } 
        finally { setIsLoading(false); }
    };

    const handleSetThumbnail = (media: MediaItem) => {
        setMediaLibrary(prev => {
            if (!prev.some(item => item.id === media.id)) {
                return [media, ...prev];
            }
            return prev;
        });
        setThumbnailId(media.id);
        setIsMediaModalOpen(false);
    };

    const selectedThumbnail = useMemo(() => {
        if (!thumbnailId) return null;
        return mediaLibrary.find(m => m.id === thumbnailId);
    }, [thumbnailId, mediaLibrary]);
    
    const handleCategoryChange = (categoryId: number, isChecked: boolean) => {
        setSelectedCategories(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (isChecked) {
                newSelected.add(categoryId);
                const ancestors = getAncestors(categoryId, allCategories);
                ancestors.forEach(id => newSelected.add(id));
            } else {
                newSelected.delete(categoryId);
                const descendants = getDescendants(categoryId, allCategories);
                descendants.forEach(id => newSelected.delete(id));
            }
            return Array.from(newSelected);
        });
    };

    const selectedCategoryObjects = useMemo(() => {
        return allCategories.filter(cat => selectedCategories.includes(cat.id))
               .sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedCategories, allCategories]);

    const runSuggestions = useCallback(() => {
        if (!tfidfProcessor) return;
        setIsSuggesting(true);
        setTimeout(() => {
            const articleText = `${title} ${summary} ${content}`;
            const suggestions = tfidfProcessor.getSuggestions(articleText);
            const maxScore = suggestions[0]?.score || 1;
            const enrichedSuggestions = suggestions.map(s => {
                const category = allCategories.find(c => c.id === s.id);
                return { ...s, name: category?.name || 'Unknown', confidence: (s.score / maxScore) * 100 };
            });
            setSuggestedCategories(enrichedSuggestions);
            setIsSuggesting(false);
        }, 50);
    }, [tfidfProcessor, title, summary, content, allCategories]);

    const applyTopSuggestions = () => {
        const top3 = suggestedCategories.slice(0, 3);
        top3.forEach(suggestion => {
            if (!selectedCategories.includes(suggestion.id)) {
                handleCategoryChange(suggestion.id, true);
            }
        });
    };

    const handleGenerateWebp = async () => {
        if (!selectedThumbnail || !user) return;
        setIsGeneratingWebp(true);
        try {
            const res = await fetch('/api/cms/media', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': user.phone },
                body: JSON.stringify({ id: selectedThumbnail.id, action: 'generate_webp' })
            });
            if (res.ok) {
                const updatedMediaItem = await res.json();
                setMediaLibrary(prev => prev.map(item => item.id === updatedMediaItem.id ? updatedMediaItem : item));
                alert(`WebP version generated successfully!`);
            } else {
                const err = await res.json();
                throw new Error(`WebP generation failed on server: ${err.error}`);
            }
        } catch (e) {
            console.error(e);
            alert(`Error during WebP generation: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
            setIsGeneratingWebp(false);
        }
    };
    
    const handleOptimizeImage = async () => {
        if (!selectedThumbnail || !user) return;
        setIsOptimizing(true);
        try {
            const img = new Image();
            img.crossOrigin = "anonymous";
            const url = new URL(selectedThumbnail.url);
            url.searchParams.set('t', Date.now().toString());
            img.src = url.toString();
            await new Promise<void>((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error(`Failed to load image from ${selectedThumbnail.url}. This is a CORS issue.`));
            });
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error("Canvas context failed");
            ctx.drawImage(img, 0, 0);
            const newDataUrl = canvas.toDataURL('image/jpeg', optimizeQuality / 100);
            const res = await fetch('/api/cms/media', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': user.phone },
                body: JSON.stringify({ id: selectedThumbnail.id, url: newDataUrl })
            });
            if (res.ok) {
                await loadResources();
                alert(`Optimized successfully!`);
            } else {
                alert('Optimization failed.');
            }
        } catch (e) {
            console.error(e);
            alert(`Error during optimization: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
            setIsOptimizing(false);
        }
    };

    const handleCopyThumbnailLink = () => {
        if (selectedThumbnail) {
            navigator.clipboard.writeText(selectedThumbnail.url);
            setThumbnailCopied(true);
            setTimeout(() => setThumbnailCopied(false), 2000);
        }
    };
    
    const renderCategoryTree = (parentId: number | null, level = 0) => {
        const children = allCategories.filter(c => c.parent_id === parentId);
        if (children.length === 0) return null;
        return (
            <div className={`flex flex-col gap-1 ${level > 0 ? 'ml-4' : ''}`}>
                {children.map(cat => (
                    <React.Fragment key={cat.id}>
                        <label className="flex items-center gap-2 p-1 hover:bg-white/5 rounded cursor-pointer group">
                             <div className={`w-4 h-4 border rounded flex items-center justify-center transition-colors shrink-0 ${selectedCategories.includes(cat.id) ? 'bg-mystic-accent border-mystic-accent' : 'border-gray-600 group-hover:border-gray-400'}`}>
                                {selectedCategories.includes(cat.id) && <Check className="w-3 h-3 text-black" />}
                            </div>
                            <input type="checkbox" className="hidden" checked={selectedCategories.includes(cat.id)} onChange={(e) => handleCategoryChange(cat.id, e.target.checked)} />
                            <span className="text-sm">{cat.name}</span>
                        </label>
                        {renderCategoryTree(cat.id, level + 1)}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const authors = useMemo(() => {
        const filtered = users.filter(u => (u.rank === 0 || u.rank === 2) && u.name.toLowerCase().includes(authorSearch.toLowerCase()));
        return filtered.sort((a,b) => a.name.localeCompare(b.name));
    }, [users, authorSearch]);

    const handleAuthorChange = (authorId: number, isChecked: boolean) => {
        setSelectedAuthorIds(prev => {
            if (isChecked) {
                return [...prev, authorId];
            } else {
                return prev.filter(id => id !== authorId);
            }
        });
    };


    return (
        <div className="h-screen overflow-hidden bg-mystic-bg text-[#e3e3e3] font-mono flex flex-col">
            <header className="flex-none h-16 border-b border-gray-800 bg-mystic-box px-6 flex items-center justify-between z-40">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(`/${adminPath}/blog`)} className="p-2 hover:bg-gray-800 rounded-full">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-bold text-white uppercase tracking-wider">
                        {id ? 'Edit Article' : 'New Article'}
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 bg-gray-800 border border-gray-700 rounded-md p-1">
                        <button
                            onClick={() => prevArticleId && navigate(`/${adminPath}/blog/edit/${prevArticleId}`)}
                            disabled={!prevArticleId}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            aria-label="Previous Article"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-xs font-mono text-gray-500 px-2 select-none">ID: {id || 'NEW'}</span>
                        <button
                            onClick={() => nextArticleId && navigate(`/${adminPath}/blog/edit/${nextArticleId}`)}
                            disabled={!nextArticleId}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            aria-label="Next Article"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                    <button onClick={handleSave} disabled={isLoading || slugStatus === 'taken'} className={`flex items-center gap-2 font-bold px-6 py-2 rounded transition-all disabled:opacity-50 ${isSuccess ? 'bg-green-600' : 'bg-mystic-accent hover:bg-mystic-accentHover text-black'}`}>
                         {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />} 
                        {isSuccess ? 'Saved' : 'Save'}
                    </button>
                </div>
            </header>
            <main className="flex-1 overflow-y-auto p-6">
                 <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 lg:col-span-9 space-y-6">
                        <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg space-y-4">
                            <input type="text" placeholder="Article Title" value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-transparent text-2xl font-bold text-white outline-none" />
                             <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm flex items-center gap-1">
                                    <Link className="w-3 h-3"/>/
                                </span>
                                <input type="text" value={slug} onChange={e => setSlug(slugify(e.target.value))} className="w-full bg-mystic-inset border border-gray-700 rounded p-2 text-white pl-8 outline-none focus:border-mystic-accent" />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs flex items-center gap-1">
                                    {slugStatus === 'checking' && <Loader2 className="w-3 h-3 animate-spin text-gray-500"/>}
                                    {slugStatus === 'available' && <CheckCircle className="w-3 h-3 text-green-500"/>}
                                    {slugStatus === 'taken' && <AlertTriangle className="w-3 h-3 text-red-500"/>}
                                </div>
                            </div>
                            {slugError && <p className="text-red-500 text-xs mt-1">{slugError}</p>}
                            <textarea placeholder="Summary..." value={summary} onChange={e => setSummary(e.target.value)} className="w-full bg-mystic-inset border border-gray-700 rounded p-2 text-white h-24 resize-none outline-none focus:border-mystic-accent"></textarea>
                        </section>
                        <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg flex flex-col min-h-[600px]">
                             <div className="flex justify-between items-center mb-4">
                                <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest">Content (HTML)</h2>
                            </div>
                            <div className="flex-1 min-h-0">
                                <CodeEditor value={content} onChange={setContent} />
                            </div>
                        </section>
                    </div>
                    <div className="col-span-12 lg:col-span-3 space-y-6">
                        <section className="bg-mystic-box border border-gray-800 p-4 rounded-lg">
                            <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4">Publishing</h2>
                            <select value={status} onChange={e => setStatus(e.target.value)} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white">
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                            </select>
                        </section>
                        
                        <section className="bg-mystic-box border border-gray-800 p-4 rounded-lg">
                            <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4">Details</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs text-gray-400 block mb-1">Authors</label>
                                    <input type="search" placeholder="Search authors..." value={authorSearch} onChange={e => setAuthorSearch(e.target.value)} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white text-sm mb-2" />
                                    <div className="max-h-32 overflow-y-auto custom-scrollbar border border-gray-700 bg-mystic-inset rounded p-2 space-y-1">
                                        {authors.map(author => (
                                            <label key={author.id} className="flex items-center gap-2 p-1 hover:bg-white/5 rounded cursor-pointer">
                                                <input type="checkbox"
                                                    checked={selectedAuthorIds.includes(author.id!)}
                                                    onChange={(e) => handleAuthorChange(author.id!, e.target.checked)}
                                                    className="form-checkbox bg-gray-800 border-gray-600 text-mystic-accent focus:ring-mystic-accent/50 shrink-0"
                                                />
                                                <span className="text-sm text-gray-300">{author.name} <span className="text-gray-600 text-xs">({author.rank === 0 ? 'Admin' : 'Expert'})</span></span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-400 block mb-1">Publication Date</label>
                                    <input 
                                        type="datetime-local" 
                                        value={publishedAt} 
                                        onChange={e => setPublishedAt(e.target.value)} 
                                        className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white text-sm" 
                                    />
                                </div>
                            </div>
                        </section>
                        
                        <section className="bg-mystic-box border border-gray-800 p-4 rounded-lg">
                            <div className="flex justify-between items-center mb-4 border-b border-gray-800 pb-2">
                                <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest">Proponowane kategorie</h2>
                                <div className="flex items-center gap-2">
                                    <button onClick={applyTopSuggestions} className="text-xs bg-mystic-accent/20 text-mystic-accent px-2 py-1 rounded hover:bg-mystic-accent/30 disabled:opacity-50" disabled={suggestedCategories.length === 0}>Zastosuj Top 3</button>
                                    <button onClick={runSuggestions} disabled={isSuggesting} className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50">
                                        {isSuggesting ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-2">
                                {suggestedCategories.length > 0 ? (
                                    suggestedCategories.map(suggestion => (
                                        <label key={suggestion.id} className="flex items-center gap-2 p-1 hover:bg-white/5 rounded cursor-pointer group">
                                            <div className={`w-4 h-4 border rounded flex items-center justify-center transition-colors shrink-0 ${selectedCategories.includes(suggestion.id) ? 'bg-mystic-accent border-mystic-accent' : 'border-gray-600 group-hover:border-gray-400'}`}>
                                                {selectedCategories.includes(suggestion.id) && <Check className="w-3 h-3 text-black" />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={selectedCategories.includes(suggestion.id)}
                                                onChange={(e) => handleCategoryChange(suggestion.id, e.target.checked)}
                                                className="hidden"
                                            />
                                            <span className="text-sm flex-1 truncate" title={suggestion.name}>{suggestion.name}</span>
                                            <span className="text-xs font-mono text-gray-500 bg-black/20 px-1.5 py-0.5 rounded">{suggestion.confidence.toFixed(0)}%</span>
                                        </label>
                                    ))
                                ) : (
                                    <p className="text-xs text-gray-600 italic text-center py-4">Brak sugestii. Kliknij odśwież.</p>
                                )}
                            </div>
                        </section>

                        <section className="bg-mystic-box border border-gray-800 p-4 rounded-lg">
                            <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4 border-b border-gray-800 pb-2">Wybrane Kategorie</h2>
                            
                            <div className="mb-4">
                                {selectedCategoryObjects.length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5">
                                        {selectedCategoryObjects.map(cat => (
                                            <div key={cat.id} className="bg-mystic-accent/10 border border-mystic-accent/30 text-mystic-accent text-xs px-2 py-1 rounded-full flex items-center gap-1.5">
                                                <span>{cat.name}</span>
                                                <button onClick={() => handleCategoryChange(cat.id, false)} className="hover:text-white">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-600 italic">Brak wybranych kategorii.</p>
                                )}
                            </div>

                            <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar pr-2">
                               {renderCategoryTree(null)}
                            </div>
                        </section>

                         <section className="bg-mystic-box border border-gray-800 p-4 rounded-lg">
                             <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4">Thumbnail</h2>
                             <div className="relative group aspect-video bg-black/20 rounded border-2 border-dashed border-gray-700 flex items-center justify-center mb-2">
                                {selectedThumbnail ? (
                                    <img src={selectedThumbnail.url} className="max-h-full max-w-full object-contain" />
                                ) : (
                                    <ImageIcon className="w-8 h-8 text-gray-600" />
                                )}
                                {selectedThumbnail && (
                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <button 
                                            onClick={() => setIsEditingThumbnail(true)}
                                            className="bg-white/20 hover:bg-white/30 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 backdrop-blur-sm"
                                        >
                                            <Settings className="w-4 h-4" /> Edit
                                        </button>
                                    </div>
                                )}
                             </div>
                             <div className="flex gap-2 text-xs">
                                <button onClick={() => setIsMediaModalOpen(true)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-1.5 rounded transition-colors">
                                    {thumbnailId ? 'Change' : 'Set Image'}
                                </button>
                                {thumbnailId && (
                                    <button onClick={() => setThumbnailId(null)} className="p-1.5 bg-red-900/50 hover:bg-red-900/80 text-red-300 rounded transition-colors">
                                        <X className="w-4 h-4"/>
                                    </button>
                                )}
                             </div>
                        </section>
                    </div>
                 </div>
            </main>
            
            {user && (
                <MediaLibraryModal 
                    isOpen={isMediaModalOpen}
                    onClose={() => setIsMediaModalOpen(false)}
                    onSelectMedia={handleSetThumbnail}
                    currentUser={user}
                    contextName={title || 'article'}
                />
            )}

            {isEditingThumbnail && selectedThumbnail && (
                <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-mystic-box border border-gray-700 w-full max-w-5xl max-h-[95vh] rounded-xl flex flex-col md:flex-row shadow-2xl relative overflow-hidden">
                        <button 
                            onClick={() => setIsEditingThumbnail(false)}
                            className="absolute top-4 right-4 p-2 bg-black/50 text-white hover:bg-red-500 hover:text-white rounded-full transition-colors z-20"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div className="flex-1 bg-black/20 flex items-center justify-center p-8 overflow-hidden relative min-h-[50vh]">
                            <img 
                                src={selectedThumbnail.url} 
                                alt="Preview" 
                                className="max-w-full max-h-full object-contain shadow-2xl" 
                            />
                        </div>
                        <div className="w-full md:w-80 bg-mystic-bg border-l border-gray-700 flex flex-col">
                            <div className="p-6 border-b border-gray-800">
                                <h3 className="text-white font-bold mb-1">Media Details</h3>
                                <div className="text-xs text-gray-500 font-mono break-all">{selectedThumbnail.url}</div>
                                {selectedThumbnail.url_webp ? (
                                    <div className="text-xs text-mystic-accent font-mono break-all mt-2">{selectedThumbnail.url_webp}</div>
                                ) : (
                                    <button
                                        onClick={handleGenerateWebp}
                                        disabled={isGeneratingWebp}
                                        className="w-full text-left mt-3 bg-black/20 hover:bg-black/40 text-mystic-accent py-2 px-3 rounded font-bold text-xs transition-colors flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {isGeneratingWebp ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                        {isGeneratingWebp ? 'Generating...' : 'Generate .webp version'}
                                    </button>
                                )}
                            </div>
                            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                                <div>
                                    <label className="text-xs uppercase text-gray-500 font-bold mb-2 block">Original Size</label>
                                    <div className="text-white font-mono text-lg">
                                        {formatBytes(selectedThumbnail.size)}
                                    </div>
                                </div>
                                <div className="p-4 bg-black/20 rounded-xl border border-gray-800">
                                      <div className="flex items-center gap-2 text-mystic-accent mb-4">
                                          <Sliders className="w-4 h-4" />
                                          <span className="text-sm font-bold uppercase">Optimization</span>
                                      </div>
                                      <label className="block text-xs text-gray-400 mb-2">Quality: {optimizeQuality}%</label>
                                      <input 
                                          type="range" 
                                          min="10" 
                                          max="100" 
                                          value={optimizeQuality}
                                          onChange={(e) => setOptimizeQuality(parseInt(e.target.value))}
                                          className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-mystic-accent mb-4"
                                      />
                                      <button 
                                          onClick={handleOptimizeImage}
                                          disabled={isOptimizing}
                                          className="w-full bg-mystic-btn hover:bg-mystic-accent hover:text-black text-white py-2 rounded font-bold text-xs transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                      >
                                          {isOptimizing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Settings className="w-3 h-3" />}
                                          {isOptimizing ? 'Compressing...' : 'Optimize & Overwrite'}
                                      </button>
                                      <p className="text-[10px] text-gray-600 mt-2 text-center leading-tight">
                                          Converts to JPEG and overwrites existing file.
                                      </p>
                                </div>
                            </div>
                            <div className="p-6 border-t border-gray-800 bg-mystic-box">
                              <button 
                                  onClick={handleCopyThumbnailLink}
                                  className="w-full flex items-center justify-center gap-2 bg-mystic-accent hover:bg-mystic-accentHover text-black px-6 py-3 rounded font-bold transition-all shadow-lg active:scale-95"
                              >
                                  {thumbnailCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                  {thumbnailCopied ? 'Link Copied' : 'Copy Link'}
                              </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArticleEditor;